/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { PreviewType, ScreenType } from 'client-code/previews';
import { ActionGroup } from 'client-code/actions';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { WatchFromSoCuAction, WatchFromSoCuActionKernel } from 'client-code/action-details';
import { VideoManager } from '../../../core/videoplayer';
import { AppUtils } from '../../../core/AppUtils';
import { UniqueIdentifier } from '../../../core/utils/UniqueIdentifier';
import { Channel } from '../../../core/entities/Channel';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { SocuDataModelImpl } from '../../../core/socu/impls';
import { Log, NFRLoggings } from '../../../core/logging';
import { PlaybackContentType } from '../../../core/socu/enums/PlaybackContentType';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import {
    APPLICATION_FEATURE_AREA,
    LEFT_QUOTE,
    MILLISECONDS_IN_A_SECOND,
    RIGHT_QUOTE,
} from '../../../config';
import { setScreenTaskController } from '../../screens/screens.reducers';
import {
    resetPlaybackParamsVideoplayer,
    startVideoPlayback,
} from '../../videoplayer/videoplayer.reducers';
import { IOverlayDialog, IOverlayOptions } from '../../overlaydialog/overlaydialogprops';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { OverlayTypes } from '../OverlayTypes';
import { showSocuErrorOverlay } from '../socuerroroverlay/socuerroroverlay.reducres';
import { NFREvents } from '../../../core/enums/NFREvents';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { AppError } from '../../../core/error';
import { ServicesClientImpl } from '../../../core/impls';
import { BrandingUiUtility } from '../../../core/utils/BrandingUiUtility';
import { TOfferPreviewCustomData } from '../../../core/types';
import { hideInfoBanners } from '../../videoplayerui/infobanner/infobanner.reducers';
import { guideHideSpinner, guideShowSpinner } from '../../guide/guide.reducers';

const GLOBAL_MOUSE_TRAP: string = 'HideVodResumeStartOverOverlay';

const TAG: string = 'VodResumeStartOverOverlay';

const deriveLastViewedPosition = (
    position: number | undefined,
    duration: number | undefined,
): number => {
    if (!position || (duration && position >= duration)) {
        return 0;
    }
    return position;
};

export const showVodResumeStartOverOverlay =
    (offerPreview: TOfferPreviewCustomData, hasDimmer: boolean = true) =>
    (dispatch: Function) => {
        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS),
        );
        const socuDataModelImpl = SocuDataModelImpl.getInstance();
        const { isCatchUp } = socuDataModelImpl.getNewSocuAction()?.kernel;
        if (!offerPreview || !Object.keys(offerPreview)) return;
        let titleText: string =
            offerPreview.contentPreview?.title ||
            Locale.getString(LocaleKeys.OVERLAY_TITLE_NOT_AVAILABLE_TEXT);
        let subtitleText: string = '';

        if (offerPreview.contentPreview?.previewType === PreviewType.EpisodePreview) {
            const { episodeNumber, seasonNumber, episodeTitle } = offerPreview.contentPreview;
            const seasonEpisodeText: string = AppUtils.generateSeasonEpisodeText(
                seasonNumber || 0,
                episodeNumber || 0,
            );
            if (seasonEpisodeText || episodeTitle) {
                subtitleText = `${seasonEpisodeText} ${LEFT_QUOTE}${
                    episodeTitle || Locale.getString(LocaleKeys.OVERLAY_TITLE_NOT_AVAILABLE_TEXT)
                }${RIGHT_QUOTE}`;
            }
        } else if (offerPreview.contentPreview?.previewType === PreviewType.MoviePreview) {
            const { movieYear } = offerPreview.contentPreview;
            const movieYearText: string = movieYear ? ` (${movieYear})` : '';
            titleText = `${titleText}${movieYearText}`;
        }

        const overlayOptions: Array<IOverlayOptions> = [
            {
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTION_RESUME_PLAYING_TEXT)}`,
                value: isCatchUp
                    ? PlaybackContentType.CATCHUP_RESUME
                    : PlaybackContentType.STARTOVER_RESUME,
            },
            {
                text: `${Locale.getString(
                    LocaleKeys.OVERLAY_OPTION_START_OVER_FROM_BEGINNING_TEXT,
                )}`,
                value: isCatchUp
                    ? PlaybackContentType.CATCHUP_BEGINNING
                    : PlaybackContentType.STARTOVER_BEGINNING,
            },
        ];
        const overlayProps: IOverlayDialog = {
            hasDimmer,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: overlayOptions,
            overlayType: OverlayTypes.VOD_RESUME_START_OVER,
            subtitle: subtitleText,
            title: titleText,
        };
        dispatch(showOverlayDialog(overlayProps));
    };

export const onKeyBackVodResumeStartOverOverlay = () => (dispatch: Function) => {
    SocuDataModelImpl.getInstance().resetNewSocuData();
    dispatch(hideOverlayDialog());
};

export const onKeyOkVodResumeStartOverOverlay = () => (dispatch: Function, getState: Function) => {
    const { gridGuide, guideHighlight, overlayHighlight, overlayDialog } = getState();

    const { channels } = gridGuide;
    const { verticalPosition } = guideHighlight;
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { options } = overlayDialog.uiProperties;

    const channel: Channel = channels[verticalPosition];
    const playbackContentType: PlaybackContentType =
        options[overlayHighlightVerticalPosition].value;

    SocuDataModelImpl.getInstance().setSocuData(playbackContentType);
    dispatch(hideOverlayDialog());
    dispatch(startVideoPlayback(channel, playbackContentType));
};

export const onKeyOkVodResumeStartOverOverlayFromLiveTv =
    () => (dispatch: Function, getState: Function) => {
        const { overlayHighlight, overlayDialog } = getState();
        const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
        const { options } = overlayDialog.uiProperties;
        const playbackContentType: PlaybackContentType =
            options[overlayHighlightVerticalPosition].value;

        SocuDataModelImpl.getInstance().setSocuData(playbackContentType);
        dispatch(hideOverlayDialog());
        dispatch(resetPlaybackParamsVideoplayer());
        VideoManager.getInstance().startPlayback({
            channel: VideoManager.getInstance().getPlayable()?.channel!,
            playbackContentType,
        });
    };

export const onMouseClickVodResumeStartOverOverlay =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideOverlayDialog());
            return;
        }

        dispatch(onKeyOkVodResumeStartOverOverlay());
    };

export const prepareVodResumeStartOverOverlayFromGuide =
    (channel: Channel) => async (dispatch: Function, getState: Function) => {
        dispatch(guideShowSpinner());
        const { offerPreview } = getState().gridGuide;
        if (!offerPreview || !Object.keys(offerPreview)) {
            dispatch(guideHideSpinner());
            return;
        }
        const socuDataModelImpl = SocuDataModelImpl.getInstance();
        const { isCatchUp, isStartOver } = socuDataModelImpl.getNewSocuAction()?.kernel;
        const networkCallId = UniqueIdentifier.getNextOttActionDetailsNetworkCallId();
        try {
            const { watchAction, actionDetailsNetworkCallId } =
                await socuDataModelImpl.fetchLastSavedPosition(
                    ScreenType.GuideRecordOverlay,
                    networkCallId,
                );
            if (
                actionDetailsNetworkCallId !== UniqueIdentifier.getOttActionDetailsNetworkCallId()
            ) {
                Log.i(TAG, 'Network call id mismatched.');
                dispatch(guideHideSpinner());
                return;
            }
            const { savedPosition, duration } = (watchAction as WatchFromSoCuAction)
                .kernel as WatchFromSoCuActionKernel;
            const derivedPosition = deriveLastViewedPosition(savedPosition, duration);
            if (derivedPosition) {
                dispatch(guideHideSpinner());
                dispatch(showVodResumeStartOverOverlay(offerPreview));
            } else {
                const playbackContentType: PlaybackContentType = isCatchUp
                    ? PlaybackContentType.CATCHUP_BEGINNING
                    : PlaybackContentType.STARTOVER_BEGINNING;
                socuDataModelImpl.setSocuData(playbackContentType);
                dispatch(guideHideSpinner());
                dispatch(startVideoPlayback(channel, playbackContentType));
            }
        } catch (error: any) {
            const services = new ServicesClientImpl();
            (error as AppError).setDescription(
                BrandingUiUtility.formatDescription((error as AppError).getDescription(), {
                    CATCHUP_NAME: isStartOver
                        ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                        : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                    ERROR_CODE: (error as AppError).getCode(),
                }),
            );
            NFRLoggings.log(NFREvents.FAILURE_LOADING_VOD_OTT_ACTION_DETAILS, error);
            Diagnostics.error(TAG, 'Error while fetching vodOttActionDetails');
            Diagnostics.error(TAG, 'stacktrace', error);
            Log.e(TAG, 'Error while fetching vodOttActionDetails');
            Log.e(TAG, 'stacktrace', error);
            dispatch(guideHideSpinner());
            dispatch(showSocuErrorOverlay(error));
        }
    };

export const prepareVodResumeStartOverOverlayFromLiveTv =
    () => async (dispatch: Function, getState: Function) => {
        dispatch(
            setScreenTaskController(
                ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS,
            ),
        );
        const { channel } = getState().videoPlayer;
        const { airingOffers, offerPreview } = VideoManager.getInstance().getMetadata();
        if (!offerPreview || !Object.keys(offerPreview)) return;
        if (!airingOffers || airingOffers.length === 0) return;
        // TODO: We need to find better approach to handle errors
        const sThrowable: string = 'fetchActionList network call id mis-matched';
        const services = new ServicesClientImpl();
        // TODO: We need to use or eleminate socu data model. Below calculation should be part of
        // model.
        const currentTime = Date.now();
        const startTime = new Date(airingOffers[0].startTime).getTime();
        const programEndTime = startTime + airingOffers![0].duration * MILLISECONDS_IN_A_SECOND;
        const currentlyAiring = startTime <= currentTime && currentTime < programEndTime;

        NFRLoggings.log(NFREvents.START_LOADING_ACTIONS_LIST);

        const networkCallId = UniqueIdentifier.getNextActionsNetworkCallId();
        const oSocuDataModelImpl = SocuDataModelImpl.getInstance();
        oSocuDataModelImpl
            .fetchActionList({
                channelNumber: channel.channelNumber,
                groups: [ActionGroup.Watch],
                offerId: airingOffers[0].offerId,
                screenType: APPLICATION_FEATURE_AREA.PLAYER,
                actionsNetworkCallId: networkCallId,
            })
            .then((actionList) => {
                return actionList.actionsNetworkCallId === networkCallId;
            })
            .then((status) => {
                if (status) {
                    return oSocuDataModelImpl.fetchLastSavedPosition(ScreenType.LiveTvBanner);
                }
                throw new Error(sThrowable);
            })
            .then(({ watchAction }) => {
                const { screenTaskController, previousScreenTaskController } = getState().screens;
                if (
                    screenTaskController ===
                    ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS
                ) {
                    dispatch(setScreenTaskController(previousScreenTaskController));
                } else {
                    Log.i(TAG, 'Discard resume overlay');
                    return;
                }
                dispatch(resetPlaybackParamsVideoplayer());
                dispatch(hideInfoBanners());
                const { savedPosition, duration } = (watchAction as WatchFromSoCuAction)
                    .kernel as WatchFromSoCuActionKernel;
                const derivedPosition = deriveLastViewedPosition(savedPosition, duration);
                if (derivedPosition) {
                    dispatch(showVodResumeStartOverOverlay(offerPreview, false));
                    return true;
                }
                const { isCatchUp } = oSocuDataModelImpl.getNewSocuAction()?.kernel;
                const playbackContentType: PlaybackContentType = isCatchUp
                    ? PlaybackContentType.CATCHUP_BEGINNING
                    : PlaybackContentType.STARTOVER_BEGINNING;
                oSocuDataModelImpl.setSocuData(playbackContentType);
                VideoManager.getInstance().startPlayback({
                    channel: VideoManager.getInstance().getPlayable()?.channel || <Channel>{},
                    playbackContentType,
                });
                return false;
            })
            .catch((error) => {
                const { screenTaskController, previousScreenTaskController } = getState().screens;
                if (
                    screenTaskController ===
                    ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS
                ) {
                    dispatch(setScreenTaskController(previousScreenTaskController));
                } else {
                    Log.i(TAG, 'Discard resume overlay');
                    return;
                }
                const errorMsg: string = error.message;
                if (!errorMsg?.includes(sThrowable)) {
                    (error as AppError).setDescription(
                        BrandingUiUtility.formatDescription((error as AppError).getDescription(), {
                            CATCHUP_NAME: currentlyAiring
                                ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                                : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                            ERROR_CODE: (error as AppError).getCode(),
                        }),
                    );
                    dispatch(showSocuErrorOverlay(error, false));
                }
                Log.e(TAG, 'stacktrace', error);
                Diagnostics.error(TAG, 'stacktrace', error);
            });
    };
